import React, { useState } from 'react';

const NoAnswerClientsList = ({ records, onEdit, onDelete, onViewHistory, onQuickAction, currentUser }) => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const getStatusColor = (status) => {
    switch (status) {
      case 'pendiente':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'en_proceso':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completado':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelado':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pendiente':
        return '⏳';
      case 'en_proceso':
        return '🔄';
      case 'completado':
        return '✅';
      case 'cancelado':
        return '❌';
      default:
        return '❓';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pendiente':
        return 'Pendiente';
      case 'en_proceso':
        return 'En Proceso';
      case 'completado':
        return 'Completado';
      case 'cancelado':
        return 'Cancelado';
      default:
        return status;
    }
  };

  const filteredRecords = records.filter(record => {
    const matchesFilter = filter === 'all' || record.status === filter;
    const matchesSearch = !searchTerm || 
      record.nombre_cliente?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.celula?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.campana?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.agente_asignado?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('es-ES');
    } catch (error) {
      return 'N/A';
    }
  };

  if (records.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <div className="text-gray-400 text-6xl mb-4">📞</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No hay clientes registrados
        </h3>
        <p className="text-gray-600">
          Comienza agregando el primer cliente que no contesta.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Filtros y búsqueda */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Buscar por cliente, célula, campaña o agente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="all">Todos los estados</option>
              <option value="pendiente">Pendientes</option>
              <option value="en_proceso">En Proceso</option>
              <option value="completado">Completados</option>
              <option value="cancelado">Cancelados</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lista de registros */}
      <div className="divide-y divide-gray-200">
        {filteredRecords.map((record) => (
          <div key={record.id} className="p-6 hover:bg-gray-50 transition-colors duration-200">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">
                    {record.nombre_cliente}
                  </h3>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(record.status)}`}>
                    <span className="mr-1">{getStatusIcon(record.status)}</span>
                    {getStatusText(record.status)}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-600">
                  <div>
                    <span className="font-medium text-gray-900">Célula:</span>
                    <div>{record.celula || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">Campaña:</span>
                    <div>{record.campana || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">Agente:</span>
                    <div>{record.agente_asignado || 'N/A'}</div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">Último Contacto:</span>
                    <div>{formatDate(record.fecha_ultimo_contacto)}</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3 text-sm text-gray-600">
                  <div>
                    <span className="font-medium text-gray-900">Intentos:</span>
                    <div>{record.intentos_contacto || 0} intentos</div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">Fecha Creación:</span>
                    <div>{formatDate(record.fecha_creacion)}</div>
                  </div>
                </div>

                {record.motivo && (
                  <div className="mt-3">
                    <span className="font-medium text-gray-900 text-sm">Motivo:</span>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                      {record.motivo}
                    </p>
                  </div>
                )}

                {record.detalles_adicionales && (
                  <div className="mt-2">
                    <span className="font-medium text-gray-900 text-sm">Detalles:</span>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                      {record.detalles_adicionales}
                    </p>
                  </div>
                )}
              </div>

              {/* Botones de acción */}
              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={() => onQuickAction(record)}
                  className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-all duration-200"
                  title="Acción Rápida"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </button>
                <button
                  onClick={() => onViewHistory(record)}
                  className="p-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-all duration-200"
                  title="Ver Historial"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
                <button
                  onClick={() => onEdit(record)}
                  className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200"
                  title="Editar"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={() => onDelete(record.id)}
                  className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200"
                  title="Eliminar"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredRecords.length === 0 && (
        <div className="p-8 text-center">
          <div className="text-gray-400 text-4xl mb-4">🔍</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No se encontraron resultados
          </h3>
          <p className="text-gray-600">
            Intenta ajustar los filtros o términos de búsqueda.
          </p>
        </div>
      )}
    </div>
  );
};

export default NoAnswerClientsList;
